import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from 'components/Elements';
import { useNavigate } from 'react-router-dom';

const plans = [
  {
    name: 'Starter',
    price: 'UGX 33,000',
    period: '/mo',
    desc: 'Perfect for individuals and small projects starting with Ashita.',
    features: ['1 Website', 'Basic CMS', 'Template Library', 'Community Support'],
    highlight: false,
  },
  {
    name: 'Growth',
    price: 'UGX 107,000',
    period: '/mo',
    desc: 'Ideal for startups and growing teams building multiple sites.',
    features: [
      '5 Websites',
      'Advanced CMS',
      'Feature Marketplace Access',
      'Integrations (Email / SMS)',
      'Priority Support',
    ],
    highlight: true,
  },
  {
    name: 'Business',
    price: 'UGX 292,000',
    period: '/mo',
    desc: 'For organizations running large-scale CMS infrastructure.',
    features: [
      'Unlimited Websites',
      'Full Feature Marketplace',
      'Advanced Integrations',
      'Role-Based Permissions',
      'Performance Monitoring',
    ],
    highlight: false,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'Custom infrastructure and engineering support.',
    features: [
      'Dedicated Infrastructure',
      'Custom Feature Development',
      'Private Deployments',
      'Advanced Security',
      'Dedicated Support',
    ],
    highlight: false,
  },
];

export const PricingPreview = () => {
  const navigate = useNavigate();
  return (
    <section id="pricing" className="py-28 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 just-me">
            Simple, Transparent Pricing
          </h2>

          <p className="text-slate-400 max-w-2xl mx-auto">
            Start small and scale your CMS infrastructure as your business grows. Subscribe to
            features and integrations whenever you need them.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
              className={`relative p-8 rounded-2xl border backdrop-blur-xl

              ${
                plan.highlight
                  ? 'border-amber-400 bg-slate-900 shadow-2xl scale-[1.04]'
                  : 'border-slate-800 bg-slate-900'
              }

              `}
            >
              {plan.highlight && (
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2
                  bg-amber-400 text-black text-xs font-semibold px-3 py-1 rounded-full"
                >
                  Most Popular
                </div>
              )}

              <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>

              <p className="text-slate-400 text-sm mb-6">{plan.desc}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold">{plan.price}</span>

                <span className="text-slate-400 ml-1">{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-amber-400" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                className="w-full"
                variant={plan.highlight ? 'solid' : 'ghost'}
                onClick={() => navigate(`/start-building?plan=${plan.name}`)}
              >
                Get Started
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
